import { useRegulatorMe } from "../../queries/regulator-portal/useRegulatorMe";
import { hasRegulatorPortalSession } from "../../utils/tokenUtils";
import { isDevModeEnv } from "../../hooks/useIsDevMode";

/**
 * `absent`    — no usable regulator token to ask `/me` with (missing, expired,
 *               or junk). The guard clears leftover storage and returns to
 *               sign-in so a leftover key cannot ping-pong.
 * `resolving` — `/me` in flight.
 * `denied`    — `/me` answered, and the caller is not a regulator this client's
 *               portal is open to. The guard clears the stale session rather
 *               than the route table, otherwise a leftover token would
 *               ping-pong between the catch-all and `RedirectIfAuthenticated`.
 *               HTTP 403 from `/me` is this state: TAS collapses audience /
 *               flag / permission / revoked into one authorization_error.
 *               Pending approval is HTTP 200 + status.
 * `pending`   — regulator awaiting approval; holding screen.
 * `approved`  — regulator with an open portal.
 */
export type RegulatorPortalSessionState =
  | "absent"
  | "resolving"
  | "denied"
  | "pending"
  | "approved";

export const useRegulatorPortalSession = (): RegulatorPortalSessionState => {
  const hasSession = hasRegulatorPortalSession();
  const { data, isPending, isError } = useRegulatorMe(hasSession);

  if (!hasSession) return "absent";
  if (isError) return "denied";
  if (isPending) return "resolving";
  if (!data?.isRegulator) return "denied";
  // Dev/test environments may reach the portal before the client-enablement
  // flag is provisioned — but only on top of a regulator `/me`, never instead
  // of one.
  if (!data.portalEnabled && !isDevModeEnv()) return "denied";
  return data.status === "approved" ? "approved" : "pending";
};
